import React from "react";
import { FileText, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import ConfidenceBadge from "@/components/business/ConfidenceBadge";
import { inr } from "@/lib/format";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

function ext(filename = "") {
  return filename.split(".").pop().toLowerCase();
}

export default function DocumentViewer({ document: doc, className }) {
  if (!doc) return null;

  const fileExt = ext(doc.file_name);
  const isPdf = fileExt === "pdf";
  const isImage = IMAGE_EXTS.includes(fileExt);
  const hasUrl = !!doc.url;

  const fields = doc.extracted
    ? Object.entries(doc.extracted).filter(([k]) => !k.startsWith("_"))
    : [];
  const confidence = doc.extracted?._confidence;

  return (
    <div className={cn("grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6", className)}>
      {/* Document preview pane */}
      <div className="flex flex-col min-h-[300px] rounded-lg border border-border overflow-hidden bg-muted/30">
        {hasUrl && isPdf && (
          <iframe
            src={doc.url}
            title={doc.file_name}
            className="flex-1 w-full"
            style={{ minHeight: 420 }}
            data-testid="doc-iframe"
          />
        )}
        {hasUrl && isImage && (
          <img
            src={doc.url}
            alt={doc.file_name}
            className="w-full object-contain max-h-[500px]"
            data-testid="doc-image"
          />
        )}
        {!hasUrl && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center" data-testid="doc-no-preview">
            <FileText className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground max-w-xs">
              Document preview is not available — AI-extracted fields are shown alongside.
            </p>
          </div>
        )}
        {hasUrl && (
          <div className="border-t border-border px-3 py-2 flex justify-end shrink-0">
            <a
              href={doc.url}
              download={doc.file_name}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              data-testid="doc-download"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Download
            </a>
          </div>
        )}
      </div>

      {/* Extracted fields pane */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
          AI-extracted fields
        </div>

        {fields.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">No fields extracted yet.</p>
        ) : (
          <div className="mt-3 space-y-0" data-testid="doc-fields">
            {fields.map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-4 border-b border-border/50 py-1.5 text-sm"
              >
                <span className="text-muted-foreground capitalize shrink-0">
                  {k.replace(/_/g, " ")}
                </span>
                <span className="font-medium text-right num-rupee">
                  {typeof v === "number" ? inr(v) : String(v)}
                </span>
              </div>
            ))}
          </div>
        )}

        {confidence !== undefined && (
          <div className="mt-4" data-testid="doc-confidence">
            <p className="text-xs text-muted-foreground mb-1.5">AI confidence</p>
            <ConfidenceBadge value={Math.round(confidence * 100)} variant="bar" />
          </div>
        )}
      </div>
    </div>
  );
}
